/**
 * Обновление состояния освоенности учебной единицы.
 *
 * Персональная модель навыка обновляется по каждому нажатию (ТЗ FR-EN-01,
 * §7.1) — но не по одному: нажатия собираются в наблюдение по единице и
 * применяются им, иначе запись состояния шла бы в пути нажатия.
 *
 * Состояние агрегированное: сырые нажатия не хранятся. Обновление
 * инкрементальное — прежние значения не пересчитываются заново, потому что
 * исходных наблюдений уже нет (ТЗ §11.2).
 */

import type { MasteryStat, MasteryUnit } from '@type2x/shared-contracts';

import type { LearningConfig } from './config/priority.v1.js';
import { clamp01, normalizeRange } from './normalize.js';

/** Наблюдение за одной единицей в рамках сессии. */
export interface UnitObservation {
  readonly attempts: number;
  readonly errors: number;
  /** Попытки, выполненные верно с первого раза. */
  readonly firstTryCorrect: number;
  readonly corrections: number;
  readonly medianLatencyMs: number;
  readonly p95LatencyMs: number;
}

/** Вес нового наблюдения при экспоненциальном сглаживании. */
const OBSERVATION_WEIGHT = 0.35;

/** Смешивает прежнее и новое значение с постоянным весом. */
function blend(previous: number, next: number, weight = OBSERVATION_WEIGHT): number {
  return previous * (1 - weight) + next * weight;
}

/**
 * Оценка освоенности, 0..1.
 *
 * Учитываются три вещи: доля верных попыток, скорость реакции и отсутствие
 * исправлений. Скорость, достигнутая исправлениями, не поощряется (ТЗ §7.7).
 */
export function computeMasteryScore(
  stat: Pick<MasteryStat, 'errorRate' | 'correctionRate' | 'medianLatencyMs'>,
  config: LearningConfig,
): number {
  const correctness = 1 - clamp01(stat.errorRate);
  const cleanliness = 1 - clamp01(stat.correctionRate);
  const speed =
    1 -
    normalizeRange(stat.medianLatencyMs, config.hesitationThresholdMs, config.hesitationCeilingMs);

  return clamp01(correctness * 0.6 + speed * 0.25 + cleanliness * 0.15);
}

/**
 * Применяет наблюдение к состоянию единицы.
 *
 * @param stat прежнее состояние;
 * @param observation итог сессии по этой единице;
 * @param config конфигурация алгоритма;
 * @param now календарная метка (ISO 8601).
 */
export function applyObservation(
  stat: MasteryStat,
  observation: UnitObservation,
  config: LearningConfig,
  now: string,
): MasteryStat {
  if (observation.attempts <= 0) return stat;

  const attempts = stat.attempts + observation.attempts;
  const firstTryCorrect = stat.firstTryCorrect + observation.firstTryCorrect;

  const sessionErrorRate = observation.errors / observation.attempts;
  const sessionCorrectionRate = observation.corrections / observation.attempts;

  const errorRate =
    stat.attempts === 0 ? sessionErrorRate : blend(stat.errorRate, sessionErrorRate);
  const correctionRate =
    stat.attempts === 0 ? sessionCorrectionRate : blend(stat.correctionRate, sessionCorrectionRate);
  const medianLatencyMs =
    stat.attempts === 0
      ? observation.medianLatencyMs
      : blend(stat.medianLatencyMs, observation.medianLatencyMs);
  const p95LatencyMs =
    stat.attempts === 0
      ? observation.p95LatencyMs
      : blend(stat.p95LatencyMs, observation.p95LatencyMs);

  const flawless = observation.errors === 0 && observation.corrections === 0;
  const consecutiveSuccesses = flawless ? stat.consecutiveSuccesses + 1 : 0;

  const next: MasteryStat = {
    ...stat,
    attempts,
    firstTryCorrect,
    errorRate: clamp01(errorRate),
    correctionRate: clamp01(correctionRate),
    medianLatencyMs,
    p95LatencyMs,
    consecutiveSuccesses,
    lastSeenAt: now,
    masteryScore: 0,
  };

  return { ...next, masteryScore: computeMasteryScore(next, config) };
}

/** Освоена ли единица: высокая оценка и устойчивая серия успехов. */
export function isMastered(stat: MasteryStat, config: LearningConfig, threshold: number): boolean {
  return stat.masteryScore >= threshold && stat.consecutiveSuccesses >= config.successesToMaster;
}

/** Ключ учебной единицы: вид и содержание однозначно её определяют. */
export function unitKey(unit: Pick<MasteryUnit, 'kind' | 'content'>): string {
  return `${unit.kind}:${unit.content}`;
}
